import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Play, CheckCircle2, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function ProgramPlayer() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [player, setPlayer] = useState(null);

  const load = () => api.get(`/me/enrolments/${eid}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [eid]);

  const openDay = async (d) => {
    if (!d.unlocked) return;
    if (d.has_workout) { navigate(`/app/programs/${eid}/day/${d.day}/workout`); return; }  // whole card -> Guided Workout player
    try {
      const { data: v } = await api.get(`/me/enrolments/${eid}/days/${d.day}/stream`);
      setPlayer({ day: d, video: v });
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object") {
        const when = det.available_at ? new Date(det.available_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
        toast.error(`${det.message || "Locked"}${when ? ` — unlocks ${when}` : ""}`);
      } else {
        toast.error(det || "Cannot play now");
      }
    }
  };

  const complete = async (day) => {
    try {
      await api.post(`/me/enrolments/${eid}/days/${day}/complete`);
      toast.success("Marked complete! +10 pts");
      setPlayer(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-10 text-muted-foreground">Loading…</div>;
  const { enrolment, program, days } = data;

  return (
    <div data-testid="program-player" className="px-4 md:px-8 pb-16 pt-2 max-w-6xl">
      <Link to="/app/programs" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-5"><ArrowLeft size={14} /> My Programs</Link>
      <span className="text-[11px] uppercase tracking-widest text-primary font-bold">{enrolment.level} · {enrolment.language}</span>
      <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight leading-none mt-2">{program.title}</h1>
      <p className="text-muted-foreground mt-2 text-sm">{program.duration_days} days · 1 new day unlocks daily at midnight IST</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-8">
        {days.map((d) => (
          <button key={d.id} data-testid={`day-${d.day}`} onClick={() => openDay(d)}
            className={`text-left border rounded-xl p-5 transition-colors ${d.unlocked ? "border-border bg-card hover:border-primary" : "border-border bg-card opacity-60 cursor-not-allowed"}`}>
            <div className="flex items-center justify-between">
              <span className="font-display font-800 text-2xl">Day {d.day}</span>
              {d.done ? <CheckCircle2 size={20} className="text-primary" /> : d.unlocked ? <Play size={20} className="text-primary" /> : <Lock size={18} className="text-muted-foreground" />}
            </div>
            <h3 className="font-bold mt-2">{d.title}</h3>
            <p className="text-xs text-muted-foreground mt-1">~24–25 min</p>
            {!d.unlocked && <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-2"><Clock size={11} /> Unlocks {d.available_at ? new Date(d.available_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : `${d.available_after_hours}h after enrolment`}</div>}
            {d.unlocked && d.has_workout && (
              <span data-testid={`start-workout-${d.day}`}
                className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
                <Zap size={12} /> Guided Workout
              </span>
            )}
          </button>
        ))}
      </div>

      {player && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-4" onClick={() => setPlayer(null)}>
          <div className="bg-card border border-border rounded-xl max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex justify-between items-center">
              <h3 className="font-display font-800 text-2xl">Day {player.day.day} — {player.day.title}</h3>
              <button onClick={() => setPlayer(null)} className="text-muted-foreground">✕</button>
            </div>
            <div className="aspect-video bg-black grid place-items-center">
              {player.video.ready ? (
                player.video.provider === "bunny" ? (
                  <iframe src={player.video.url} title={`Day ${player.day.day}`} className="w-full h-full" allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowFullScreen />
                ) : (
                  <video src={player.video.url} controls autoPlay controlsList="nodownload" className="w-full h-full" />
                )
              ) : (
                <div className="text-center px-6"><p className="font-bold">Video not yet available</p><p className="text-xs text-muted-foreground mt-2">The coach is finalising this session — check back shortly.</p></div>
              )}
            </div>
            <div className="p-5 flex justify-between items-center">
              <p className="text-sm text-muted-foreground">{player.day.description}</p>
              <button onClick={() => complete(player.day.day)} data-testid="mark-day-complete" className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-xs uppercase font-bold">{player.day.done ? "Done ✓" : "Mark Complete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
